import { snakeCase, startCase } from 'lodash/fp';
import { FieldErrors } from 'react-hook-form';

import {
  DEFAULT_AI_MODEL,
  ExtractionPanelForm,
  MatchRequirement,
} from 'dashboard/components/ExtractionPanel/util';
import {
  CompositeComparisonMethodType,
  DefinitionType,
  ExtractionFieldType,
  ExtractionRequest,
  ExtractionType,
  ExtractorType,
  GuidedExtractionRequestV3,
  GuidedPreset,
  MatchLevel,
} from 'dashboard/models/document';
import { ComparisonMethodType, NormalizationMethodType } from 'dashboard/models/verification-check';
import { assertNever } from 'lib/utils/types';

export const guidedExtractionRequestToPresetGenericFormState = (
  extractionRequest: GuidedExtractionRequestV3,
): Partial<ExtractionPanelForm> => {
  const subfields = extractionRequest.subfields.map((s) => s['source-field']);
  return {
    version: '3.0',
    fieldName: startCase(extractionRequest['field-name']),
    extractionType: extractionRequest['extraction-type'],
    extractorType: ExtractorType.InquiryFieldComparison,
    fieldType: extractionRequest['field-type'],
    extractionCategory: subfields[0],
    matchRequirement:
      extractionRequest.preset === GuidedPreset.GenericStrict
        ? MatchRequirement.Strict
        : extractionRequest.preset === GuidedPreset.GenericLoose
          ? MatchRequirement.Loose
          : MatchRequirement.LooseAi,
  };
};

export const guidedPresetGenericFormStateToExtractionRequest = (
  data: ExtractionPanelForm,
): ExtractionRequest => {
  switch (data.matchRequirement) {
    case MatchRequirement.Loose:
      return {
        version: '3.0',
        'extraction-type': ExtractionType.Guided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'definition-type': DefinitionType.Preset,
        preset: GuidedPreset.GenericLoose,
        subfields: [
          {
            'source-field': data.extractionCategory,
            'search-method': {
              method: 'full_string',
            },
            criteria: [
              {
                'match-level': MatchLevel.Partial,
                'logic-composition': {
                  type: 'logic_composition',
                  'match-level-minimum': MatchLevel.Partial,
                  operator: 'comparison',
                  'comparison-method': {
                    type: ComparisonMethodType.StringSimilarity,
                    'full-match-threshold': 0.9,
                    'partial-match-threshold': 0.7,
                    'max-char-difference': 4,
                    'match-level-minimum': MatchLevel.Partial,
                  },
                  'normalization-methods': [
                    {
                      type: NormalizationMethodType.FoldCharacters,
                    },
                    {
                      type: NormalizationMethodType.LowercaseLetters,
                    },
                  ],
                  'augmentation-methods': [],
                },
              },
            ],
          },
        ],
        'match-criteria': [
          {
            'match-level': MatchLevel.Partial,
            'subfields-composite-comparison-method': {
              type: CompositeComparisonMethodType.LogicComposition,
              operator: 'and',
              components: [
                {
                  type: CompositeComparisonMethodType.LogicComposition,
                  operator: 'comparison',
                  components: [],
                  'comparison-method': {
                    type: CompositeComparisonMethodType.SubfieldMatchFound,
                    'match-level-minimum': MatchLevel.Partial,
                    subfield: data.extractionCategory,
                  },
                },
              ],
            },
          },
        ],
      };
    case MatchRequirement.LooseAi:
      return {
        version: '3.0',
        'extraction-type': ExtractionType.Guided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'definition-type': DefinitionType.Preset,
        preset: GuidedPreset.GenericLooseAi,
        subfields: [
          {
            'source-field': data.extractionCategory,
            'search-method': {
              method: 'full_string',
            },
            criteria: [
              {
                'match-level': MatchLevel.Partial,
                'logic-composition': {
                  type: 'logic_composition',
                  'match-level-minimum': MatchLevel.Partial,
                  operator: 'comparison',
                  'comparison-method': {
                    type: ComparisonMethodType.Ai,
                    model: DEFAULT_AI_MODEL,
                    'match-level-minimum': MatchLevel.Partial,
                  },
                  'normalization-methods': [],
                  'augmentation-methods': [],
                },
              },
            ],
            'ai-model': DEFAULT_AI_MODEL,
          },
        ],
        'match-criteria': [
          {
            'match-level': MatchLevel.Partial,
            'subfields-composite-comparison-method': {
              type: CompositeComparisonMethodType.LogicComposition,
              operator: 'and',
              components: [
                {
                  type: CompositeComparisonMethodType.LogicComposition,
                  operator: 'comparison',
                  components: [],
                  'comparison-method': {
                    type: CompositeComparisonMethodType.SubfieldMatchFound,
                    'match-level-minimum': MatchLevel.Partial,
                    subfield: data.extractionCategory,
                  },
                },
              ],
            },
          },
        ],
      };
    case MatchRequirement.Strict:
      return {
        version: '3.0',
        'extraction-type': ExtractionType.Guided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'definition-type': DefinitionType.Preset,
        preset: GuidedPreset.GenericStrict,
        subfields: [
          {
            'source-field': data.extractionCategory,
            'search-method': {
              method: 'full_string',
            },
            criteria: [
              {
                'match-level': MatchLevel.Partial,
                'logic-composition': {
                  type: 'logic_composition',
                  'match-level-minimum': MatchLevel.Partial,
                  operator: 'comparison',
                  'comparison-method': {
                    type: ComparisonMethodType.StringSimilarity,
                    'full-match-threshold': 1.0,
                    'partial-match-threshold': 1.0,
                    'max-char-difference': 4,
                    'match-level-minimum': MatchLevel.Partial,
                  },
                  'normalization-methods': [
                    {
                      type: NormalizationMethodType.LowercaseLetters,
                    },
                  ],
                  'augmentation-methods': [],
                },
              },
              {
                'match-level': MatchLevel.Full,
                'logic-composition': {
                  type: 'logic_composition',
                  'match-level-minimum': MatchLevel.Full,
                  operator: 'comparison',
                  'comparison-method': {
                    type: ComparisonMethodType.StringSimilarity,
                    'full-match-threshold': 1.0,
                    'partial-match-threshold': 1.0,
                    'max-char-difference': 4,
                    'match-level-minimum': MatchLevel.Partial,
                  },
                  'normalization-methods': [
                    {
                      type: NormalizationMethodType.LowercaseLetters,
                    },
                  ],
                  'augmentation-methods': [],
                },
              },
            ],
          },
        ],
        'match-criteria': [
          {
            'match-level': MatchLevel.Partial,
            'subfields-composite-comparison-method': {
              type: CompositeComparisonMethodType.LogicComposition,
              operator: 'comparison',
              components: [],
              'comparison-method': {
                type: CompositeComparisonMethodType.SubfieldMatchFound,
                'match-level-minimum': MatchLevel.Partial,
                subfield: data.extractionCategory,
              },
            },
          },
          {
            'match-level': MatchLevel.Full,
            'subfields-composite-comparison-method': {
              type: CompositeComparisonMethodType.LogicComposition,
              operator: 'comparison',
              components: [],
              'comparison-method': {
                type: CompositeComparisonMethodType.SubfieldMatchFound,
                'match-level-minimum': MatchLevel.Full,
                subfield: data.extractionCategory,
              },
            },
          },
        ],
      };
    default:
      assertNever(data.matchRequirement as never);
  }
};

export const validateGuidedExtractionGenericPanelForm = (
  data: ExtractionPanelForm,
  unconfigurableInquiryFieldNames: string[],
) => {
  const errorRequired = { type: 'required', message: 'Required' };
  const errors: FieldErrors<ExtractionPanelForm> = {};

  if (data?.extractionCategory == null) {
    errors.extractionCategory = errorRequired;
  }
  if (unconfigurableInquiryFieldNames.includes(snakeCase(data.extractionCategory))) {
    errors.extractionCategory = {
      type: 'type',
      message: 'The corresponding inquiry field is of an unconfigurable type',
    };
  }
  return errors;
};
